/**
 * The mosque's contacts — imams, reciters, speakers, technicians, suppliers, authorities.  MKK module 13 «Kontakter»
 * (B1, 2026-10-24).
 *
 * "Who did we book for Ramadan last year, and what was his number?" A contact is a person (or a firm) the mosque works
 * with: a name, a category, an organisation, a role, a phone, an e-mail, languages, free notes for staff, and tags.
 * Staff only by default: never shown to members, never public — except a contact staff tick «Vis i appen» (`showInApp`,
 * owner 2026-10-30, with the person's consent), whose name, role, phone and e-mail then appear on the app's «Kontakt»
 * page (mosque-contact.js publicPeople). Behind the `contacts_directory` capability (privacy scope of its own: these are
 * third parties' personal data). Two permissions (access.js):
 *   contacts.read     see and search the directory, call or write from the phone
 *   contacts.manage   add, change, archive and restore
 *
 * A contact is never deleted here: it is ARCHIVED (kept, hidden from the list) and may be restored. Nothing deletes an
 * archived contact automatically: contacts are kept, with no automatic deletion (owner 2026-10-25; retention.js).
 *
 * Pure: no I/O, no clock — "now" is passed in.
 */
const { isId, CONTROL, oneLine, text } = require('./fields');
const Csv = require('./csv');
const TZ = require('./tz');

/*
 * The categories. The seven of 056, and seven more since 074 («Kontakter» made manageable, owner 2026-10-31): naat-khwan,
 * scholar, politician, the press, a sponsor, a volunteer, and the board of another mosque. «other» stays last.
 */
const CATEGORIES = ['imam', 'qari', 'naat_khwan', 'speaker', 'scholar', 'technician', 'supplier', 'authority', 'politician', 'media', 'sponsor',
  'volunteer', 'board_other_mosque', 'other'];
const CATEGORY_NO = { imam: 'Imam', qari: 'Resitator', naat_khwan: 'Naat-khwan', speaker: 'Foredragsholder', scholar: 'Lærd (alim)',
  technician: 'Tekniker', supplier: 'Leverandør', authority: 'Myndighet', politician: 'Politiker', media: 'Presse og medier', sponsor: 'Sponsor',
  volunteer: 'Frivillig', board_other_mosque: 'Styret i en annen moské', other: 'Annet' };
const CATEGORY_EN = { imam: 'Imam', qari: 'Reciter', naat_khwan: 'Naat khwan', speaker: 'Speaker', scholar: 'Scholar (alim)',
  technician: 'Technician', supplier: 'Supplier', authority: 'Authority', politician: 'Politician', media: 'Press and media', sponsor: 'Sponsor',
  volunteer: 'Volunteer', board_other_mosque: 'Board of another mosque', other: 'Other' };
const MAX_NAME = 120;
const MAX_ORGANISATION = 120;
const MAX_ROLE = 120;
const MAX_LANGUAGES = 120;
const MAX_NOTES = 2000;
const MAX_TAGS = 200;
const MAX_EMAIL = 254;
const MAX_QUERY = 100;
const MAX_CONTACTS = 2000;
/** The fields staff may send. */
const FIELDS = ['name', 'category', 'organisation', 'roleTitle', 'phone', 'email', 'languages', 'notes', 'tags', 'showInApp'];

/**
 * A phone number as the platform keeps it (the rule of forms.js and auth.js): spaces, dashes, dots and parentheses
 * dropped, "00" read as "+", a bare 8-digit number read as Norwegian. → "+4741290118" or null when it is not a number.
 */
function normalisePhone(input) {
  if (input === undefined || input === null || input === '') return null;
  let s = String(input).replace(/[\s\-().]/g, '');
  if (s.startsWith('00')) s = '+' + s.slice(2);
  if (!s.startsWith('+')) { if (!/^\d{8}$/.test(s)) return null; s = '+47' + s; }
  return /^\+\d{8,15}$/.test(s) ? s : null;
}
/** An e-mail address: trimmed, lower-cased, one "@", a dot in the domain. → the address or null. */
function normaliseEmail(input) {
  if (input === undefined || input === null) return null;
  const s = String(input).trim().toLowerCase();
  if (!s || s.length > MAX_EMAIL) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/.test(s) ? s : null;
}
/** Tags as typed ("ramadan, eid,Eid ") → ['ramadan', 'eid']: trimmed, lower-cased, no duplicates, no empties. */
function normaliseTags(input) {
  if (input === undefined || input === null) return [];
  const list = Array.isArray(input) ? input : String(input).split(',');
  const out = [];
  for (const raw of list) {
    const s = String(raw ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

/**
 * A contact as staff send it — every field checked; unknown fields refused. `base` is the contact as it is now for a
 * change (PATCH), or nothing for a new one. → { ok, value: {name, category, organisation, roleTitle, phone, email,
 * languages, notes, tags, showInApp}, changed: [field…] } | { ok: false, errors }
 * `showInApp` is a real boolean (a new contact: false) — never "true" or 1: a contact goes public only on purpose.
 */
function input(src0, base = null) {
  const src = src0 && typeof src0 === 'object' && !Array.isArray(src0) ? src0 : null;
  if (!src) return { ok: false, errors: { contact: 'Ugyldig kontakt.' } };
  const unknown = Object.keys(src).filter((k) => !FIELDS.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const cur = base || { name: undefined, category: 'other', organisation: null, roleTitle: null, phone: null, email: null, languages: null, notes: null, tags: [], showInApp: false };
  const pick = (k) => (Object.prototype.hasOwnProperty.call(src, k) ? src[k] : cur[k]);
  const errors = {};
  const out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('name', oneLine(pick('name'), 'et navn', MAX_NAME, true));
  const category = pick('category');
  if (!CATEGORIES.includes(category)) errors.category = 'Velg en kategori.'; else out.category = category;
  take('organisation', oneLine(pick('organisation'), 'organisasjonen', MAX_ORGANISATION, false));
  take('roleTitle', oneLine(pick('roleTitle'), 'rollen', MAX_ROLE, false));
  const phoneIn = pick('phone');
  if (phoneIn === undefined || phoneIn === null || String(phoneIn).trim() === '') out.phone = null;
  else {
    const p = normalisePhone(phoneIn);
    if (p) out.phone = p; else errors.phone = 'Telefonnummeret må være 8 siffer (norsk) eller starte med + og landkode.';
  }
  const emailIn = pick('email');
  if (emailIn === undefined || emailIn === null || String(emailIn).trim() === '') out.email = null;
  else {
    const e = normaliseEmail(emailIn);
    if (e) out.email = e; else errors.email = 'E-postadressen ser ikke riktig ut.';
  }
  take('languages', oneLine(pick('languages'), 'språk', MAX_LANGUAGES, false));
  take('notes', text(pick('notes'), 'notatet', MAX_NOTES));
  const tagsIn = pick('tags');
  if (tagsIn !== undefined && tagsIn !== null && typeof tagsIn !== 'string' && !Array.isArray(tagsIn)) errors.tags = 'Stikkord må være tekst.';
  else {
    const tags = normaliseTags(tagsIn);
    if (tags.join(', ').length > MAX_TAGS) errors.tags = `Stikkordene kan ha maks ${MAX_TAGS} tegn til sammen.`;
    else if (tags.some((t) => CONTROL.test(t))) errors.tags = 'Stikkord kan ikke ha kontrolltegn.';
    else out.tags = tags;
  }
  const show = pick('showInApp');
  if (show === undefined) out.showInApp = false;
  else if (typeof show !== 'boolean') errors.showInApp = '«Vis i appen» må være ja eller nei.';
  else out.showInApp = show;
  if (Object.keys(errors).length) return { ok: false, errors };
  const same = (k) => (k === 'tags' ? (base[k] || []).join() === out[k].join() : k === 'showInApp' ? Boolean(base[k]) === out[k]
    : (base[k] ?? null) === (out[k] ?? null));
  const changed = base ? FIELDS.filter((k) => !same(k)) : FIELDS.filter((k) => (k === 'tags' ? out[k].length : k === 'showInApp' ? out[k] : out[k] != null));
  return { ok: true, value: out, changed };
}

/** Tags as stored (one text column, comma-separated) and back. */
const tagsToText = (tags) => (tags && tags.length ? tags.join(', ') : null);
const tagsFromText = (s) => normaliseTags(s);

/**
 * The list's filters, from a query string's values. Unknown values are refused (400), never ignored.
 * → { ok, value: { q, category, archived: 'no'|'yes'|'all' } } | { ok: false, message }
 */
const ARCHIVED = ['no', 'yes', 'all'];
/** «Sist kontaktet» (074): never; not in the last 30 days (never included); not in the last 90 days (never included). */
const TOUCHED = ['never', 'over30', 'over90'];
const TOUCHED_NO = { never: 'Aldri', over30: 'Ikke på 30 dager', over90: 'Ikke på 90 dager' };
/** The list's order (074): by name A–Å; most recently contacted first (never contacted last); newest added first. */
const SORTS = ['name', 'touched', 'newest'];
const SORT_NO = { name: 'Navn A–Å', touched: 'Sist kontaktet', newest: 'Nyeste' };
/**
 * The list's filters, from a query string's values (the list and the CSV export read the same ones). Unknown values are
 * refused (400), never ignored. tags: comma-separated, every one must be on the contact (and).
 * → { ok, value: { q, category, archived: 'no'|'yes'|'all', tags: [], touched: null|…, sort: 'name'|… } } | { ok: false, message }
 */
function filter(q = {}) {
  const get = (k) => (q[k] === undefined || q[k] === null || q[k] === '' ? undefined : String(q[k]));
  const f = { q: get('q') || '', category: get('category') || null, archived: get('archived') || 'no', tags: normaliseTags(get('tags')),
    touched: get('touched') || null, sort: get('sort') || 'name' };
  if (f.category && !CATEGORIES.includes(f.category)) return { ok: false, message: 'Ugyldig kategori.' };
  if (!ARCHIVED.includes(f.archived)) return { ok: false, message: 'Ugyldig arkivfilter.' };
  if (f.q.length > MAX_QUERY) return { ok: false, message: `Søket kan ha maks ${MAX_QUERY} tegn.` };
  if (f.tags.join(', ').length > MAX_TAGS) return { ok: false, message: 'For mange stikkord i filteret.' };
  if (f.touched && !TOUCHED.includes(f.touched)) return { ok: false, message: 'Ugyldig filter for sist kontaktet.' };
  if (!SORTS.includes(f.sort)) return { ok: false, message: 'Ugyldig sortering.' };
  f.q = f.q.replace(/\s+/g, ' ').trim().toLowerCase();
  return { ok: true, value: f };
}

/** The SQL LIKE pattern for a search (the database does the search, no FTS): %, _ and \ in the text are escaped. */
function likePattern(q) { return `%${String(q).replace(/[\\%_]/g, (c) => '\\' + c)}%`; }

/** Whether a contact passes a filter: search over name, organisation, role and tags; category; archived. */
function matches(c, f) {
  if (f.archived === 'no' ? c.archivedAt : f.archived === 'yes' && !c.archivedAt) return false;
  if (f.category && c.category !== f.category) return false;
  if (f.q) {
    const hay = [c.name, c.organisation, c.roleTitle, ...(c.tags || [])].filter(Boolean).join('\n').toLowerCase();
    if (!hay.includes(f.q)) return false;
  }
  return true;
}

/** The list's order: by name (Norwegian collation), then id — stable and total. Archived contacts after the live ones. */
function sort(list) {
  return [...list].sort((a, b) => (a.archivedAt ? 1 : 0) - (b.archivedAt ? 1 : 0)
    || String(a.name).localeCompare(String(b.name), 'nb') || String(a.id).localeCompare(String(b.id)));
}

/** Whole days from the calendar day `a` to `b` (both YYYY-MM-DD). */
const daysBetween = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

/**
 * The list as the panel shows it, and as the export writes it (074): `rows` filtered by `f` (filter()'s value — q,
 * category, archived, tags, touched) and put in `f.sort`'s order. `today` is the mosque-local day (YYYY-MM-DD) and
 * `zone` the mosque's zone: «Ikke på 30 dager» counts calendar days in the mosque's zone from the day of the last
 * touch (`lastTouchedAt`, an instant). A contact never contacted is in «Aldri» and in both «Ikke på … dager».
 * Pure: no clock — today is passed in.
 */
function filterRows(rows, f0 = {}, { today, zone = 'Europe/Oslo' } = {}) {
  const f = { q: '', category: null, archived: 'no', tags: [], touched: null, sort: 'name', ...f0 };
  if (f.touched && f.touched !== 'never' && !/^\d{4}-\d{2}-\d{2}$/.test(String(today || ''))) throw new Error('contacts.filterRows: today (YYYY-MM-DD) is required');
  const dayOf = (iso) => TZ.dateIn(new Date(iso), zone);
  const touchedOk = (c) => {
    if (!f.touched) return true;
    if (!c.lastTouchedAt) return true;           // never contacted: in all three
    if (f.touched === 'never') return false;
    return daysBetween(dayOf(c.lastTouchedAt), today) > (f.touched === 'over30' ? 30 : 90);
  };
  const out = (rows || []).filter((c) => matches(c, f) && touchedOk(c) && f.tags.every((t) => (c.tags || []).includes(t)));
  const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'nb') || String(a.id).localeCompare(String(b.id));
  const live = (a, b) => (a.archivedAt ? 1 : 0) - (b.archivedAt ? 1 : 0);
  if (f.sort === 'touched') return out.sort((a, b) => live(a, b) || String(b.lastTouchedAt || '').localeCompare(String(a.lastTouchedAt || '')) || byName(a, b));
  if (f.sort === 'newest') return out.sort((a, b) => live(a, b) || String(b.createdAt || '').localeCompare(String(a.createdAt || '')) || byName(a, b));
  return sort(out);
}

/** Every tag on the list, once, in Norwegian order — the panel's tag chips. */
function allTags(rows) {
  const set = new Set();
  for (const c of rows || []) for (const t of c.tags || []) set.add(t);
  return [...set].sort((a, b) => a.localeCompare(b, 'nb'));
}

/**
 * The export (074, GET /admin/contacts/export.csv): one line per contact of the filtered list — name, category, role,
 * organisation, phone, e-mail, languages, tags, in the app, last contacted (the mosque-local day). NEVER the notes
 * (staff's own words about a person stay in the panel). Semicolons, CRLF, typed text through Csv.cell (a formula start
 * gets an apostrophe).
 */
/*
 * A phone in the export (review P2): stored E.164 and checked by the server (+ and digits), so it is never a formula —
 * but «+47…» through Csv.cell would get an apostrophe Excel shows. Norwegian numbers are written spaced
 * («+47 412 90 118»: text to Excel, the + kept), others as they are; anything else (never stored) through Csv.cell.
 */
function csvPhone(p) {
  if (!p) return '';
  const no = /^\+47(\d{3})(\d{2})(\d{3})$/.exec(p);
  if (no) return Csv.num(`+47 ${no[1]} ${no[2]} ${no[3]}`);
  return /^\+\d{8,15}$/.test(p) ? Csv.num(p) : Csv.cell(p);
}
const CSV_HEAD = ['Navn', 'Kategori', 'Rolle', 'Organisasjon', 'Telefon', 'E-post', 'Språk', 'Stikkord', 'I appen', 'Sist kontaktet', 'Arkivert'];
function csv(rows, { zone = 'Europe/Oslo' } = {}) {
  const t = Csv.cell;
  return Csv.table(CSV_HEAD, (rows || []).map((c) => Csv.line([t(c.name), t(CATEGORY_NO[c.category] || c.category), t(c.roleTitle || ''),
    t(c.organisation || ''), csvPhone(c.phone), t(c.email || ''), t(c.languages || ''), t((c.tags || []).join(', ')), t(c.showInApp ? 'ja' : 'nei'),
    t(c.lastTouchedAt ? TZ.dateIn(new Date(c.lastTouchedAt), zone) : ''), t(c.archivedAt ? 'ja' : 'nei')])));
}

/**
 * A contact made from a person in the shared register (Fellesregister, 074 + platform 104): what the person agreed may
 * be copied — the name, the phone, the e-mail, the languages and the first of their categories this mosque knows
 * («Annet» if none). Never a note, a tag or «Vis i appen». → the fields for input().
 */
function fromDirectory(p) {
  const category = (p.categories || []).find((c) => CATEGORIES.includes(c)) || 'other';
  const lang = p.languages ? String(p.languages).replace(/\s+/g, ' ').trim().slice(0, MAX_LANGUAGES) : null;
  return { name: String(p.name || '').slice(0, MAX_NAME), category, phone: p.phone || null, email: p.email || null, languages: lang || null };
}

/** Archive: kept, hidden from the list. An archived contact cannot be archived again. → { ok, value } | { ok: false, code } */
function archive(c, at) {
  if (c.archivedAt) return { ok: false, code: 'already_archived', message: 'Kontakten er allerede arkivert.' };
  return { ok: true, value: { ...c, archivedAt: at } };
}
/** Restore: back on the list. A live contact cannot be restored. */
function restore(c) {
  if (!c.archivedAt) return { ok: false, code: 'not_archived', message: 'Kontakten er ikke arkivert.' };
  return { ok: true, value: { ...c, archivedAt: null } };
}

/** The audit's view of a contact: its name, category, whether it is archived and whether it is shown in the app — never
 *  phone, e-mail or notes. (Who made a person public, and when, is the record that matters for their consent.) */
const auditView = (c) => ({ contact_name: c.name, contact_category: c.category, contact_archived: Boolean(c.archivedAt),
  contact_in_app: Boolean(c.showInApp), ...(c.directoryPersonId ? { contact_from_directory: true } : {}) });

module.exports = { CATEGORIES, CATEGORY_NO, CATEGORY_EN, TOUCHED, TOUCHED_NO, SORTS, SORT_NO, CSV_HEAD, FIELDS, MAX_NAME, MAX_ORGANISATION, MAX_ROLE, MAX_LANGUAGES, MAX_NOTES, MAX_TAGS, MAX_EMAIL, MAX_QUERY, MAX_CONTACTS,
  isId, normalisePhone, normaliseEmail, normaliseTags, tagsToText, tagsFromText, input, filter, filterRows, allTags, csv, fromDirectory, likePattern, matches, sort, archive, restore, auditView };
